import {
    ref,
    set,
    get,
    onValue,
    update,
    remove,
    DataSnapshot
} from 'firebase/database';
import { database } from '../lib/firebase.ts';
import { GameRoom, Player, Guess } from '../types/game.ts';

export class GameService {
    // Oda oluştur
    static async createRoom(roomCode: string, hostPlayer: Player): Promise<GameRoom> {
        const room: GameRoom = {
            id: roomCode,
            code: roomCode,
            targetWord: '', // Host kelimeyi belirleyecek
            players: [{ ...hostPlayer, isHost: true }],
            gameStatus: 'waiting',
            maxGuesses: 6,
            currentGuessIndex: 0,
            createdAt: new Date().toISOString(),
            hostId: hostPlayer.id,
            wordSubmitted: false
        };

        const roomRef = ref(database, `rooms/${roomCode}`);
        await set(roomRef, room);
        return room;
    }

    // Odaya katıl
    static async joinRoom(roomCode: string, player: Player): Promise<GameRoom> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const snapshot = await get(roomRef);

        if (!snapshot.exists()) {
            throw new Error('Oda bulunamadı');
        }

        const room = snapshot.val() as GameRoom;

        // Oyuncu zaten var mı kontrol et
        const existingPlayer = room.players.find(p => p.id === player.id);
        if (existingPlayer) {
            return room; // Zaten katılmış
        }

        // Oyuncuyu ekle (guest olarak)
        const updatedPlayers = [...room.players, { ...player, isHost: false }];
        const updatedRoom = {
            ...room,
            players: updatedPlayers,
            updatedAt: new Date().toISOString()
        };

        await set(roomRef, updatedRoom);
        return updatedRoom;
    }

    // Oda durumunu güncelle
    static async updateRoom(roomCode: string, updates: Partial<GameRoom>): Promise<GameRoom> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const updateData = {
            ...updates,
            updatedAt: new Date().toISOString()
        };

        await update(roomRef, updateData);

        // Güncellenmiş odayı döndür
        const snapshot = await get(roomRef);
        return snapshot.val() as GameRoom;
    }

    // Oyuncu tahminini güncelle
    static async updatePlayerGuess(
        roomCode: string,
        playerId: string,
        guesses: Guess[]
    ): Promise<GameRoom> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const snapshot = await get(roomRef);

        if (!snapshot.exists()) {
            throw new Error('Oda bulunamadı');
        }

        const room = snapshot.val() as GameRoom;
        const updatedPlayers = room.players.map((p: Player) =>
            p.id === playerId ? { ...p, guesses } : p
        );

        const updatedRoom = {
            ...room,
            players: updatedPlayers,
            updatedAt: new Date().toISOString()
        };

        await set(roomRef, updatedRoom);
        return updatedRoom;
    }

    // Oda durumunu dinle (real-time)
    static subscribeToRoom(roomCode: string, callback: (room: GameRoom) => void) {
        const roomRef = ref(database, `rooms/${roomCode}`);

        const unsubscribe = onValue(roomRef, (snapshot: DataSnapshot) => {
            if (snapshot.exists()) {
                callback(snapshot.val() as GameRoom);
            }
        });

        return unsubscribe;
    }

    // Oda durumunu al
    static async getRoom(roomCode: string): Promise<GameRoom | null> {
        try {
            const roomRef = ref(database, `rooms/${roomCode}`);
            const snapshot = await get(roomRef);

            if (!snapshot.exists()) {
                return null;
            }

            return snapshot.val() as GameRoom;
        } catch (error) {
            console.error('Oda alınırken hata:', error);
            return null;
        }
    }

    // Host kelimeyi belirler
    static async submitWord(roomCode: string, word: string): Promise<GameRoom> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const snapshot = await get(roomRef);

        if (!snapshot.exists()) {
            throw new Error('Oda bulunamadı');
        }

        const room = snapshot.val() as GameRoom;

        const updatedRoom = {
            ...room,
            targetWord: word.toUpperCase(),
            wordSubmitted: true,
            gameStatus: 'playing' as const,
            updatedAt: new Date().toISOString()
        };

        await set(roomRef, updatedRoom);
        return updatedRoom;
    }

    // Oyuncu bilgilerini güncelle
    static async updatePlayer(roomCode: string, playerId: string, updates: Partial<Player>): Promise<void> {
        const roomRef = ref(database, `rooms/${roomCode}`);
        const snapshot = await get(roomRef);

        if (!snapshot.exists()) {
            throw new Error('Oda bulunamadı');
        }

        const room = snapshot.val() as GameRoom;
        const updatedPlayers = room.players.map((p: Player) =>
            p.id === playerId ? { ...p, ...updates } : p
        );

        const updatedRoom = {
            ...room,
            players: updatedPlayers,
            updatedAt: new Date().toISOString()
        };

        await set(roomRef, updatedRoom);
    }


    // Host çıkış yaptığında odayı sil
    static async deleteRoom(roomCode: string): Promise<void> {
        try {
            const roomRef = ref(database, `rooms/${roomCode}`);
            await remove(roomRef);
            console.log(`Oda silindi: ${roomCode}`);
        } catch (error) {
            console.error('Oda silme hatası:', error);
        }
    }

    // Oda silindiğinde oyuncuları ana ekrana yönlendir
    static async notifyRoomDeleted(roomCode: string): Promise<void> {
        try {
            // Oda silindiğinde oyunculara bildirim gönder
            const notificationRef = ref(database, `notifications/${roomCode}`);
            await set(notificationRef, {
                type: 'room_deleted',
                message: 'Host oyundan ayrıldı. Ana ekrana yönlendiriliyorsunuz.',
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            console.error('Bildirim gönderme hatası:', error);
        }
    }


    // Aboneliği iptal et
    static unsubscribe(subscription: any) {
        if (subscription && typeof subscription === 'function') {
            subscription();
        }
    }
}
